function ticksToDateCode(ticks: any) {
  const dateChars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const dateCodeStartSecs = 1630472400; //arbitary; Wed Sep 01 2021 00:00:00 GMT-0500 (Central Daylight Time)
  let seconds = Math.floor(ticks / 1000);
  let delta = seconds - dateCodeStartSecs;
  if (delta <= 0) throw new Error('ticksToDateCode: ticks value passed is too early'); //avoid infinite loop
  let dateCode = [];
  while (delta) {
    let rem = delta % dateChars.length;
    dateCode.unshift(dateChars.charAt(rem));
    delta = Math.floor(delta / dateChars.length);
  }
  return dateCode.join('');
}

/**
 * Checks for the existence of a (pin) code in a string.
 * Attempts to detect standard and non-standard (pin) code variations like:
 *   1234
 *   1 2 3 4
 *   1_2_3_4
 *   one two three four
 *   onetwothreefour
 *   123four
 *   One two three four
 */
function textIncludesCode(code: string, text: string): boolean {
  const numToWord: { [key: string]: string } = {
    '0': 'zero',
    '1': 'one',
    '2': 'two',
    '3': 'three',
    '4': 'four',
    '5': 'five',
    '6': 'six',
    '7': 'seven',
    '8': 'eight',
    '9': 'nine',
  };
  const numbersArr = code.split('').map((n) => numToWord[n]);
  const cleanText = text.toLowerCase().replace(/[^A-Z0-9]+/gi, '');

  // Compares adjacent numbers and words
  const codeFound = (codeArr: string[]): boolean => {
    let startIdx: number;

    const numFoundAtIndex = (num: string, idx: number) => {
      const found = cleanText.charAt(idx) === num;
      if (found) {
        startIdx++;
        return true;
      }
      return false;
    };

    const wordFoundAtIndex = (word: string, idx: number) => {
      const found = cleanText.slice(idx, idx + word.length) === word;
      if (found) {
        startIdx = idx + word.length;
        return true;
      }
      return false;
    };

    return codeArr.every((num) => {
      if (!startIdx) {
        const numIdx = cleanText.indexOf(num, 0);
        const wordIdx = cleanText.indexOf(numToWord[num], 0);

        if (numIdx >= 0) {
          startIdx = numIdx;
        } else if (wordIdx >= 0) {
          startIdx = wordIdx;
        } else {
          return false;
        }
      }

      if (numFoundAtIndex(num, startIdx) || wordFoundAtIndex(numToWord[num], startIdx)) return true;
      return false;
    });
  };

  // Detect adjacent numbers and words
  if (codeFound(code.split('')) || codeFound(code.split('').reverse())) return true;

  // Detect unordered words
  if (numbersArr.every((n) => cleanText.includes(n))) return true;

  // Detect unordered numbers
  if (code.split('').every((n) => cleanText.includes(n))) return true;

  return false;
}

/**
 * Flattens values in a Map of pin code values
 */
function flattenPinCode(code: Map<number, number | null>): string {
  return code
    ? Array.from(code.values())
        .filter((v) => v !== null)
        .join('')
        .trim()
    : '';
}

/**
 * Creates an empty pin code Map (to store pin code input by user)
 */
function initializePinCode(length: number): Map<number, null> {
  const code = new Map();
  for (let i = 0; i < length; i++) {
    code.set(i, null);
  }
  return code;
}

/**
 * Coverts a pin code (array of numbers) to a Map
 */
function createPinCodeMap(codeValues: (number | null)[]): Map<number, number | null> {
  const code = new Map();
  codeValues.forEach((c, index) => code.set(index, c));
  return code;
}

/**
 * Compares two Maps to match keys/values
 */
function mapsAreEqual(map1: Map<any, any>, map2: Map<any, any>): boolean {
  return map1.size === map2.size && Array.from(map1.keys()).every((key) => map1.get(key) === map2.get(key));
}

export { createPinCodeMap, flattenPinCode, initializePinCode, mapsAreEqual, textIncludesCode, ticksToDateCode };
